<template>
  <div class="product-price-and-cart">
    <div class="product-price-section">
      <div class="price-row">
        <c-product-price
          :productId="productId"
          :priceinfo="priceinfo"
          :quantity="quantity"
          :hideVatInfo="true"
          :inherit="inherit"
        />
        <div v-if="showVatInfo" class="price-vat-info">
          <span class="price-vat-text">{{ $T('pricewithoutvat') }}</span>
          <b-icon
            v-b-tooltip="$T('pricetooltiptext')"
            icon="b-info-circle"
            class="price-vat-icon"
          />
        </div>
      </div>
    </div>

    <div class="product-availability-section">
      <div v-if="isOutOfStock" class="availability-status not-available">
          <b-icon icon="b-x-circle-fill" class="availability-icon"/>
          <span class="availability-text">{{ $T('notavailable') }}</span>
        </div>
        <div v-else class="availability-status available">
          <b-icon icon="b-check-circle-fill" class="availability-icon"/>
          <span class="availability-text">{{ $T('available') }}</span>
      </div>
    </div>

    <div v-if="statusMessage" class="product-status-message">
      <div
        class="status-message-box"
        @click="onStatusMessageClick"
        :class="statusMessageBoxClasses"
      >
          <p class="status-message-text">
          <b-icon class="status-message-icon" icon="b-bell-fill"/>
          {{ statusMessage }}
        </p>
        <p v-if="orderStatus === PRODUCT_ORDER_STATUS.LOCATION_REQUIRED" class="status-message-link">
          <slot name="manage-locations-link"></slot>
        </p>
      </div>
    </div>

    <div v-if="isAvailable" class="product-cart-section">
      <c-product-basketbutton
        :productId="productId"
        :quantityinfo="quantityinfo"
        :quantityPostFixSingular="quantityPostFixSingular"
        :quantityPostFixPlural="quantityPostFixPlural"
        :requiredAdditionalPropertiesValidation="requiredAdditionalPropertiesValidation"
        :errorToastTitle="errorToastTitle"
        :translations="TRANSLATIONS.productbuybutton"
        :useI18n="true"
      >
        <slot name="basketbutton-text">{{ $T('addToBasket') }}</slot>
        <template #additionalconfig="{ setAdditionalBasketConfig }">
          <slot name="additionalconfig" :setAdditionalBasketConfig="setAdditionalBasketConfig"/>
        </template>
      </c-product-basketbutton>
    </div>
  </div>
</template>

<script>
import { PRODUCT_ORDER_STATUS } from '@/constants';
import { getProductRestockSubscriptionStatus, toggleProductRestockSubscription } from '@/assets/js/helper/product-restock-subscription';

export default {
  name: 'ProductPriceAndCart',
  props: {
    productId: {
      type: String,
      required: true
    },
    orderStatus: {
      type: String,
      default: PRODUCT_ORDER_STATUS.LOCATION_REQUIRED,
      validator: value => Object.values(PRODUCT_ORDER_STATUS).includes(value)
    },
    priceinfo: {
      type: Object,
      default: () => ({
        currency: 'CHF',
        price: 0,
        priceUnassembled: 0,
        priceOriginal: 0
      })
    },
    quantity: {
      type: Number,
      default: 1
    },
    quantityinfo: {
      type: Object,
      default: () => ({})
    },
    quantityPostFixSingular: {
      type: String,
      default: ''
    },
    quantityPostFixPlural: {
      type: String,
      default: ''
    },
    requiredAdditionalPropertiesValidation: {
      type: Object,
      default: () => ({})
    },
    errorToastTitle: {
      type: String,
      default: ''
    },
    hideVatInfo: {
      type: Boolean,
      default: false
    },
    inherit: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      PRODUCT_ORDER_STATUS,
      isRestockSubscribed: false
    }
  },
  computed: {
    isLoggedIn () {
      return !!this.$store.getters['profile/getUserId']
    },
    isOutOfStock () {
      return this.orderStatus === PRODUCT_ORDER_STATUS.OUT_OF_STOCK
    },
    isAvailable () {
      return this.orderStatus === PRODUCT_ORDER_STATUS.AVAILABLE
    },
    showVatInfo () {
      return !this.hideVatInfo
    },
    statusMessage () {
      if (this.orderStatus === PRODUCT_ORDER_STATUS.LOCATION_REQUIRED) {
        return this.$T('orderLocationRequired')
      } else if (this.orderStatus === PRODUCT_ORDER_STATUS.NOT_ORDERABLE_ELIGIBILITY) {
        return this.$T('notOrderableEligibility')
      } else if (this.orderStatus === PRODUCT_ORDER_STATUS.OUT_OF_STOCK) {
        return this.$T('notifyMe')
      }
      return null
    },
    statusMessageBoxClasses () {
      return {
        'badge-secondary': !this.isOutOfStock || !this.isRestockSubscribed,
        'badge-primary': this.isOutOfStock && this.isRestockSubscribed,
        'out-of-stock': this.isOutOfStock
      }
    }
  },
  watch: {
    productId: 'refreshRestockSubscriptionStatus',
    orderStatus: 'refreshRestockSubscriptionStatus',
    isLoggedIn: 'refreshRestockSubscriptionStatus'
  },
  mounted () {
    this.refreshRestockSubscriptionStatus()
  },
  methods: {
    async refreshRestockSubscriptionStatus () {
      if (!this.productId || !this.isOutOfStock || !this.isLoggedIn) {
        this.isRestockSubscribed = false
        return
      }
      const { subscribed } = await getProductRestockSubscriptionStatus(this.productId)
      this.isRestockSubscribed = subscribed
    },
    async onStatusMessageClick () {
      if (!this.isOutOfStock || !this.isLoggedIn) {
        return
      }

      const result = await toggleProductRestockSubscription({
        productExternalId: this.productId,
        toast: this.$bvToast,
        t: key => this.$T(key)
      })
      if (result.success && typeof result.subscribed === 'boolean') {
        this.isRestockSubscribed = result.subscribed
      }
    }
  }
}
</script>

<style lang="scss">
$product-price-and-cart-gap: $spacer * 1.2 !default;

$product-price-section-gap: $spacer * 0.3 !default;

$price-row-gap: $spacer * 0.5 !default;
$price-row-align-items: center !default;

$price-vat-info-gap: $spacer * 0.25 !default;
$price-vat-text-font-size: $font-size-sm !default;
$price-vat-text-color: $gray-600 !default;
$price-vat-icon-size: 1em !default;
$price-vat-icon-color: $success !default;

$availability-status-gap: $spacer * 0.25 !default;
$availability-icon-size: 1em !default;
$availability-icon-color: $success !default;
$availability-text-font-size: inherit !default;
$availability-text-color: inherit !default;

$status-message-box-padding: $spacer !default;
$status-message-box-border-radius: $border-radius !default;
$status-message-text-font-size: inherit !default;
$status-message-link-font-size: $font-size-sm !default;
$status-message-link-color: inherit !default;
$status-message-link-margin-top: $spacer * 0.5 !default;

.product-price-and-cart {
  display: flex;
  flex-direction: column;
  gap: $product-price-and-cart-gap;

  .product-price-section {
    display: flex;
    flex-direction: column;
    gap: $product-price-section-gap;

    .price-row {
      display: flex;
      align-items: $price-row-align-items;
      gap: $price-row-gap;
    }

    .price-vat-info {
      display: flex;
      align-items: center;
      gap: $price-vat-info-gap;

      .price-vat-text {
        font-size: $price-vat-text-font-size;
        color: $price-vat-text-color;
      }

      .price-vat-icon {
        font-size: $price-vat-icon-size;
        color: $price-vat-icon-color;
      }
    }
  }

  .product-availability-section {
    .availability-status {
      display: flex;
      align-items: center;
      gap: $availability-status-gap;

      &.available {
        .availability-icon {
          font-size: $availability-icon-size;
          color: $availability-icon-color;
        }

        .availability-text {
          font-size: $availability-text-font-size;
          color: $availability-text-color;
        }
      }
    }
  }

  .product-status-message {
    .status-message-box {
      padding: $status-message-box-padding;
      @include make-leaf($status-message-box-border-radius);

      .status-message-text {
        font-size: $status-message-text-font-size;
        margin: 0;
        display: flex;
        align-items: center;
        gap: $spacer * 0.5;

        .status-message-icon {
          font-size: 1.2em;
          display: none;
        }
      }


      .status-message-link {
        font-size: $status-message-link-font-size;
        color: $status-message-link-color;
        margin: $status-message-link-margin-top 0 0 0;

        a {
          color: inherit;
          text-decoration: underline;

          &:hover {
            text-decoration: none;
          }
        }
      }

      &.out-of-stock {
        cursor: pointer;

        .status-message-text{
          .status-message-icon{
            display: block;
          }
        }
      }
    }
  }

  .product-cart-section {
    width: 100%;
  }
}
</style>

